import React, { useState } from 'react';

import { ActionIcon, Button, Popover, TextInput, Tooltip, rem } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import { IconExternalLink, IconLink } from '@tabler/icons-react';

/**
 * A URL without a scheme (e.g. `example.com`) is treated by the browser as a path relative to the
 * current page, so the link resolves inside the app instead of navigating out. Default a bare
 * host/path to `https://` so it points at the intended external site. Values that already carry a
 * protocol (`https://…`, `http://…`) or a `mailto:` address are left untouched.
 */
const normalizeLinkHref = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export interface RichTextLinkControlProps {
  /**
   * Show the "open in a new tab" action inside the input so authors can verify the URL works.
   * Defaults to true. Set false for editors where opening links during authoring isn't useful.
   */
  withOpenInNewTab?: boolean;
}

/**
 * Drop-in replacement for Mantine's `RichTextEditor.Link` toolbar control.
 *
 * Like the stock control, it reads the editor and i18n labels from `RichTextEditor`'s context
 * (`useRichTextEditorContext`), so it takes no `editor` prop and must be rendered inside a
 * `<RichTextEditor>` toolbar. Pair it with `<RichTextEditor.Unlink />` for link removal.
 *
 * Differences from the stock control, by design:
 * - **No "same tab / new tab" toggle.** Whether a link opens in a new tab is decided by the
 *   editor's `Link` extension config (and the read-only renderer), not per-link here — so the
 *   stored markup stays consistent. Use `Link.configure({ HTMLAttributes: { target: '_blank' } })`
 *   if you want links to always open in a new tab.
 * - **An "open in a new tab" action** (toggle with `withOpenInNewTab`) so authors can click through
 *   and verify the URL is correct while editing.
 *
 * @example
 * <RichTextEditor editor={editor}>
 *   <RichTextEditor.Toolbar>
 *     <RichTextEditor.ControlsGroup>
 *       <RichTextLinkControl />
 *       <RichTextEditor.Unlink />
 *     </RichTextEditor.ControlsGroup>
 *   </RichTextEditor.Toolbar>
 *   <RichTextEditor.Content />
 * </RichTextEditor>
 */
const RichTextLinkControl = ({ withOpenInNewTab = true }: RichTextLinkControlProps): JSX.Element => {
  const { editor, labels } = useRichTextEditorContext();
  const [opened, { open, close }] = useDisclosure(false);
  const [url, setUrl] = useState('');
  const trimmedUrl = url.trim();

  const handleOpenPopover = (): void => {
    // Prefill with the existing link when the cursor sits on one, so editing reuses the value.
    setUrl(editor?.getAttributes('link').href ?? '');
    open();
  };

  const handleSave = (): void => {
    const href = normalizeLinkHref(url);
    const chain = editor?.chain().focus().extendMarkRange('link');
    if (href) {
      chain?.setLink({ href }).run();
    } else {
      chain?.unsetLink().run();
    }
    close();
  };

  const handleOpenInNewTab = (): void => {
    const href = normalizeLinkHref(url) || editor?.getAttributes('link').href;
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Popover opened={opened} onClose={close} withinPortal trapFocus position='bottom' shadow='md'>
      <Popover.Target>
        <RichTextEditor.Control
          onClick={handleOpenPopover}
          active={editor?.isActive('link') ?? false}
          aria-label={labels.linkControlLabel}
          title={labels.linkControlLabel}
        >
          <IconLink stroke={1.5} size={rem(16)} />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown>
        {/* Mirrors Mantine's stock link editor: one box wrapping the input and an attached Save
            button. When enabled, the open-link icon replaces their same/new-tab toggle in the
            input's right section. */}
        <div style={{ display: 'flex' }}>
          <TextInput
            type='url'
            placeholder={labels.linkEditorInputPlaceholder}
            aria-label={labels.linkEditorInputLabel}
            value={url}
            onChange={event => setUrl(event.currentTarget.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
              }
            }}
            rightSection={
              withOpenInNewTab ? (
                <Tooltip label={labels.linkEditorExternalLink} withinPortal withArrow disabled={!trimmedUrl}>
                  <ActionIcon
                    variant='subtle'
                    size='sm'
                    onClick={handleOpenInNewTab}
                    disabled={!trimmedUrl}
                    aria-label={labels.linkEditorExternalLink}
                  >
                    <IconExternalLink size={rem(14)} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              ) : undefined
            }
            styles={{
              root: { flex: 1 },
              input: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
            }}
            data-autofocus
          />
          <Button
            variant='default'
            onClick={handleSave}
            styles={{ root: { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, marginLeft: rem(-1) } }}
          >
            {labels.linkEditorSave}
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};

export default RichTextLinkControl;
